import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from './context/ThemeContext'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ApiProvider, useApi } from './context/ApiContext'
import { MaintenanceProvider } from './context/MaintenanceContext'
import { NotificationProvider } from './context/NotificationContext'
import { ConfigProvider } from './hooks/useConfig'
import { FilterSortProvider } from './hooks/useFilterSort'
import { ErrorBoundary } from './design-system'
import ProtectedRoute from './components/ProtectedRoute'
import Sidebar from './components/Sidebar'
import MaintenanceOverlay from './components/MaintenanceOverlay'
import Login from './pages/Login'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
      refetchOnWindowFocus: false,
    },
  },
})

const Dashboard = lazy(() => import('./pages/Dashboard'))
const PricingActions = lazy(() => import('./pages/PricingActions'))
const Reports = lazy(() => import('./pages/Reports'))
const Users = lazy(() => import('./pages/Users'))
const Rentals = lazy(() => import('./pages/Rentals'))
const UserPage = lazy(() => import('./pages/UserPage'))
const Config = lazy(() => import('./pages/Config'))
const BubblePlots = lazy(() => import('./pages/BubblePlots'))
const DesignSystem = lazy(() => import('./pages/DesignSystem'))

const PricingRules = lazy(() => import('./pages/PricingRules'))
const Competitors = lazy(() => import('./pages/Competitors'))

function ApiErrorBanner() {
  const { error, refreshData } = useApi()
  if (!error) return null
  return (
    <div className="api-error-banner" role="alert">
      <span>Failed to load data: {error?.message || 'Unknown error'}</span>
      <button type="button" className="api-error-banner__retry" onClick={refreshData}>Retry</button>
    </div>
  )
}

function AuthBoundMaintenanceProvider({ children }) {
  const { isAuthenticated, loading } = useAuth()
  return (
    <MaintenanceProvider isAuthenticated={loading || isAuthenticated}>
      {children}
    </MaintenanceProvider>
  )
}

function AppLayout() {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="app-loading" role="status" aria-busy="true">
        <div className="app-loading-spinner" />
        <span className="sr-only">Loading application…</span>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <div className="app-layout">
      <a href="#main-content" className="sr-only skip-link">Skip to main content</a>
      <Sidebar />
      <main id="main-content" className="app-main">
        <ApiErrorBanner />
        <Suspense fallback={<div className="app-loading" role="status" aria-busy="true"><div className="app-loading-spinner" /><span className="sr-only">Loading page…</span></div>}>
          <Routes>
            <Route path="/:dashId" element={
              <ProtectedRoute><Dashboard /></ProtectedRoute>
            } />
            <Route path="/pricing-actions" element={
              <ProtectedRoute><PricingActions /></ProtectedRoute>
            } />
            <Route path="/pricing-rules" element={
              <ProtectedRoute><PricingRules /></ProtectedRoute>
            } />
            <Route path="/override-rules" element={<Navigate to="/pricing-rules" replace />} />
            <Route path="/reports" element={
              <ProtectedRoute><Reports /></ProtectedRoute>
            } />
            <Route path="/ecr" element={
              <ProtectedRoute><Rentals /></ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute requireAdmin><Users /></ProtectedRoute>
            } />
            <Route path="/manage-account" element={
              <ProtectedRoute><UserPage /></ProtectedRoute>
            } />
            <Route path="/bubble-plots" element={
              <ProtectedRoute><BubblePlots /></ProtectedRoute>
            } />
            <Route path="/competitors" element={
              <ProtectedRoute><Competitors /></ProtectedRoute>
            } />
            <Route path="/configuration" element={
              <ProtectedRoute><Config /></ProtectedRoute>
            } />
            <Route path="/design-system" element={
              <ProtectedRoute><DesignSystem /></ProtectedRoute>
            } />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/" element={<Navigate to="/overview" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <AuthBoundMaintenanceProvider>
              <ApiProvider>
                <ConfigProvider>
                  <FilterSortProvider>
                    <NotificationProvider>
                      <MaintenanceOverlay />
                      <AppLayout />
                    </NotificationProvider>
                  </FilterSortProvider>
                </ConfigProvider>
              </ApiProvider>
            </AuthBoundMaintenanceProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
